import store from '../index';
import { addCentralizedError, setErrorModalVisible } from '../slices/nhoSlice';
import { generateErrorId, ensureRetry, extractOriginalMessage } from './errorHandlerBase';

export const NHO_API_NAMES = {
  FETCH_EMPLOYEES: 'fetchNHOEmployees',
  FETCH_PULSE_CHECK: 'fetchPulseCheckData',
  SUBMIT_TOUCHPOINT: 'submitTouchPointSurvey',
  SUBMIT_PULSE_CHECK: 'submitPulseCheck',
  FETCH_PROBATION_SUMMARY: 'fetchProbationSummary',
  FETCH_TOUCHPOINT_SURVEY: 'fetchTouchPointSurvey',
  SUBMIT_UNDERTAKING: 'submitUndertaking',
  FETCH_NHO: 'fetchNHO',
  FETCH_NHO_BY_ID: 'fetchNHOById',
  EXPORT_PDF: 'exportNhoPdf',
  FETCH_UNDERTAKING: 'fetchUndertaking',
  FETCH_STATIC_UNDERTAKING: 'fetchStaticUndertaking',
  FETCH_NOTIFICATIONS: 'fetchNotifications',
} as const;

export const getUserFriendlyErrorMessage = (
  apiName: string,
  originalMessage: string
): string => {
  // Special-case: for submit pulse check, prefer a fixed user-facing message so overlays are consistent
  if (apiName === NHO_API_NAMES.SUBMIT_PULSE_CHECK) {
    return 'Unable to submit pulse check. Please try again.';
  }

  // If caller already passed a friendly message that starts with "Unable to" or already includes a retry suggestion,
  // return it as-is to avoid double-prefixing.
  if (!originalMessage) return `Unable to fetch the data. Please try again.`;
  const trimmed = originalMessage.trim();
  if (/^Unable to/i.test(trimmed) || /Please try again\.?$/i.test(trimmed)) {
    return trimmed;
  }
  return `Unable to fetch the data. ${trimmed}`;
};

export const handleNHOErrorWithFriendlyMessage = (
  apiName: string,
  error: any,
  displayModal: boolean = true,
  context?: any
) => {
  const originalMessage = extractOriginalMessage(error);
  const friendlyMessage = getUserFriendlyErrorMessage(apiName, originalMessage);

  const centralizedError = {
    id: generateErrorId(),
    apiName,
    message: ensureRetry(friendlyMessage),
    timestamp: Date.now(),
    context: { originalMessage, ...context },
  };

  // DEBUG: Log immediately to help trace when handler is invoked in runtime
  try {
    // eslint-disable-next-line no-console
    console.debug(`[NHO Handler] dispatching centralized error: id=${centralizedError.id}, api=${apiName}, message=${centralizedError.message}`);
  } catch (e) {
    // noop - defensive
  }

  // Dispatch to NHO slice
  store.dispatch(addCentralizedError(centralizedError));
  if (displayModal) {
    store.dispatch(setErrorModalVisible(true));
  }

  console.error(`[NHO Error] ${apiName}:`, {
    userFriendlyMessage: friendlyMessage,
    originalMessage,
    errorId: centralizedError.id,
    originalError: error,
    context: centralizedError.context,
  });

  return centralizedError;
};

export const safeNHOApiCall = async (apiCall: () => Promise<any>, apiName: string, displayModal = true, context?: any) => {
  try {
    const result = await apiCall();
    return result;
  } catch (error) {
    handleNHOErrorWithFriendlyMessage(apiName, error, displayModal, context);
    return null;
  }
};
